import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronRight,
  Dumbbell,
  Loader2,
  Play,
  Calendar,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { useMe, usePlan, useWorkoutHistory } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { PortalHeader } from "@/components/portal/PortalHeader";
import { WeeklyProgressCard } from "@/components/portal/WeeklyProgressCard";
import { ExerciseCard } from "@/components/portal/ExerciseCard";
import { useStartExecution } from "@/components/portal/useStartExecution";
import {
  countWorkoutItems,
  flattenWorkoutItems,
  gifPublicUrl,
  shortDateLabel,
  summarizePrescription,
  lastExecutionByWorkout,
  suggestTodaysWorkout,
  weeklySnapshot,
} from "@/components/portal/utils";

function firstName(name: string | null | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return "Aluno";
  return trimmed.split(/\s+/)[0];
}

function avatarInitial(name: string | null | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return "A";
  return trimmed.charAt(0).toUpperCase();
}

/**
 * Tela Início: saudação + card de progresso semanal + treino sugerido pra hoje
 * (com os primeiros exercícios) + atalho "ver todos os treinos". Portada de
 * `frontend/components/portal/HomeView.tsx` (Next), trocando props/SSR pelas
 * queries React Query (useMe/usePlan/useWorkoutHistory) e router por react-router.
 */
export function HomeView() {
  const { slug = "" } = useParams();
  const me = useMe();
  const plan = usePlan();
  const history = useWorkoutHistory();
  const { startingId, startError, startWorkout } = useStartExecution();

  const workouts = plan.data?.workouts ?? [];
  const executions = history.data?.executions ?? [];

  const todayWorkout = useMemo(
    () => suggestTodaysWorkout(workouts, executions),
    [workouts, executions],
  );
  const todayItems = useMemo(
    () => (todayWorkout ? flattenWorkoutItems(todayWorkout) : []),
    [todayWorkout],
  );
  const weekly = useMemo(() => weeklySnapshot(executions), [executions]);
  const lastByWorkout = useMemo(() => lastExecutionByWorkout(executions), [executions]);

  // Esperamos perfil + plano (o histórico carrega em paralelo; sem ele o card
  // semanal mostra "0 treinos", o que é correto enquanto carrega).
  if (me.isLoading || plan.isLoading) return <Spinner fullscreen />;

  if (me.isError || plan.isError) {
    return (
      <div className="px-5 pt-8">
        <EmptyState
          icon={AlertCircle}
          title="Não consegui carregar seu início"
          description="Verifique sua conexão e tente novamente."
        />
      </div>
    );
  }

  const lastLabel = todayWorkout
    ? shortDateLabel(lastByWorkout.get(todayWorkout.id) ?? null)
    : null;

  return (
    <div className="animate-fade-in">
      <PortalHeader
        greeting={`Olá, ${firstName(me.data?.name)} 👋`}
        avatarInitial={avatarInitial(me.data?.name)}
      />

      <div className="space-y-4 px-5 pb-4">
        {startError ? (
          <div
            role="alert"
            className="rounded-2xl border border-danger/40 bg-danger/10 px-3 py-2.5 text-sm text-danger"
          >
            {startError}
          </div>
        ) : null}

        <WeeklyProgressCard
          completed={weekly.count}
          weekDots={weekly.weekDots}
          streakDays={weekly.streakDays}
        />

        {todayWorkout ? (
          <section aria-labelledby="today-workout-title">
            <div className="mb-2.5 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-accent-soft">
                  Seu treino de hoje
                </p>
                <h2 id="today-workout-title" className="font-display text-2xl text-ink">
                  {todayWorkout.title}
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  {countWorkoutItems(todayWorkout)} exercícios
                  {lastLabel ? (
                    <>
                      <span className="mx-1.5 text-faint">•</span>
                      Último: {lastLabel}
                    </>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => startWorkout(todayWorkout.id)}
                disabled={startingId != null}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-ink shadow-lg transition-all hover:bg-accent-hover active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {startingId === todayWorkout.id ? (
                  <>
                    <Loader2 size={14} className="animate-spin" aria-hidden />
                    <span>Iniciando</span>
                  </>
                ) : (
                  <>
                    <Play size={14} aria-hidden />
                    <span>Iniciar</span>
                  </>
                )}
              </button>
            </div>

            {todayItems.length === 0 ? (
              <EmptyState
                icon={Dumbbell}
                title="Este treino ainda não tem exercícios"
                description="Seu treinador vai adicionar em breve."
              />
            ) : (
              <ul className="space-y-2.5">
                {todayItems.slice(0, 4).map((item) => {
                  const summary = summarizePrescription(item);
                  return (
                    <li key={item.id}>
                      <ExerciseCard
                        name={item.line}
                        detail={summary.detail}
                        loadLabel={summary.loadLabel}
                        restSeconds={summary.restSeconds}
                        gifUrl={gifPublicUrl(item.gif_path)}
                        videoUrl={item.video_url}
                        imageUrl={item.image_urls?.[0] ?? null}
                      />
                    </li>
                  );
                })}
                {todayItems.length > 4 ? (
                  <li>
                    <Link
                      to={`/s/${slug}/treinos`}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border-subtle bg-surface px-4 py-3 text-sm font-medium text-muted transition-colors hover:border-border-strong hover:text-ink"
                    >
                      Ver todos os {todayItems.length} exercícios
                      <ChevronRight size={14} aria-hidden />
                    </Link>
                  </li>
                ) : null}
              </ul>
            )}
          </section>
        ) : (
          <EmptyState
            icon={Sparkles}
            title="Plano em construção"
            description="Quando seu treinador montar seu plano, ele aparece aqui."
          />
        )}

        {/* Atalho "Ver todos os treinos" */}
        {workouts.length > 1 ? (
          <Link
            to={`/s/${slug}/treinos`}
            className="flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-surface px-4 py-3 transition-all hover:border-border-strong hover:bg-surface-hover"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border-subtle bg-elevated">
                <Calendar size={16} className="text-accent-soft" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-semibold text-ink">Ver todos os treinos</p>
                <p className="text-xs text-muted">{workouts.length} treinos prescritos</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-faint" aria-hidden />
          </Link>
        ) : null}
      </div>
    </div>
  );
}
